import { Component, ElementRef, EventEmitter, forwardRef, Input, OnInit, Output, ViewChild } from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, Validators } from '@angular/forms';
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

// Validator für Mindestlänge
export function minLengthValidator(minLength: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        const value = control.value;
        return value && value.length < minLength ? { 'minLength': true } : null;
    };
}

// Validator für Großbuchstaben
export function uppercaseValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        const value = control.value;
        return value && !/[A-Z]/.test(value) ? { 'uppercase': true } : null;
    };
}

// Validator für Kleinbuchstaben
export function lowercaseValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        const value = control.value;
        return value && !/[a-z]/.test(value) ? { 'lowercase': true } : null;
    };
}

// Validator für Zahlen
export function numberValidator(): ValidatorFn {

    return (control: AbstractControl): ValidationErrors | null => {
        const value = control.value;
        return value && !/[0-9]/.test(value) ? { 'number': true } : null;
    };
}

// Validator für Sonderzeichen
export function specialCharacterValidator(): ValidatorFn {

    return (control: AbstractControl): ValidationErrors | null => {
        const value = control.value;
        return value && !/[!@#$%^&*(),.?":{}|<>/_+-]/.test(value) ? { 'specialCharacter': true } : null;
    };
}


@Component({
    selector: 'rr-input',
    standalone: false,

    templateUrl: './input.component.html',
    styleUrl: './input.component.scss',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => InputComponent),
            multi: true,
        }
    ]
})
export class InputComponent implements ControlValueAccessor, OnInit {
    @Input() label: string = '';
    @Input() placeholder: string = '';
    @Input() type: string = 'text';
    @Input() verifyPassword: boolean = false;
    @Input() disabled: boolean = false;
    @Input() required: boolean = false;
    @Input() multipleAdresses: boolean = false;
    @Input() autocomplete: string = 'on';

    @Output() change: EventEmitter<any> = new EventEmitter();
    @Output() keyup: EventEmitter<any> = new EventEmitter();

    @Input() FormControl: FormControl = new FormControl();
    @ViewChild('inputElement') inputElement!: ElementRef<HTMLInputElement>;

    errorClass: string = '';
    onTouched: () => void = () => { };
    onChange: (value: any) => void = () => { };

    ngOnInit() {
        if (this.type === 'password') {
            this.verifyPassword = true;
        }
        if (this.required) {
            this.FormControl.setValidators(Validators.required);
        }
        this.FormControl.valueChanges.subscribe((value) => {
            this.onChange(value);
            this.change.emit(value);
        });
        this.checkValidation();
    }

    keyUp(event: KeyboardEvent) {
        // if (this.type === 'text') {
        //   this.FormControl.setValue(this.FormControl?.value.replaceAll(/[0-9]/g, ''))
        // }
        this.keyup.emit(event);
        if (this.multipleAdresses === true) {
            this.checkEmailOrMultiEmailInField()
        }
    }

    writeValue(value: any): void {
        this.FormControl.setValue(value);
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    onInput(event: Event) {
        const inputElement = event.target as HTMLInputElement;
        this.change.emit(inputElement.value);
    }

    togglePassword() {
        this.type = this.type === 'password' ? 'text' : 'password';
    }

    async checkValidation() {
        let valid = [];
        if (this.required) {
            valid.push(Validators.required);
        }
        if (this.type === 'email') {
            valid.push(Validators.email);
        }
        if (this.type === 'number') {
            valid.push(Validators.pattern('^[0-9]*$'));
        }
        if (this.type === 'url') {
            valid.push(Validators.pattern('^(http|https)://[a-zA-Z0-9-.]+.[a-zA-Z]{2,3}(/S*)?$'));
        }

        if (this.verifyPassword === true) {
            valid.push(minLengthValidator(8));
            valid.push(uppercaseValidator());
            valid.push(lowercaseValidator());
            valid.push(numberValidator());
            valid.push(specialCharacterValidator());
            // console.log(this.FormControl.errors);

        }
        this.FormControl.setValidators(valid);
        this.FormControl.updateValueAndValidity();
        if (this.FormControl.invalid) {
            this.errorClass = 'error';
            return false;
        }
        this.errorClass = '';
        return true;
    }

    async checkEmailOrMultiEmailInField() {
        const value = this.FormControl.value;

        if (this.type === 'email' && value) {
            let validme = true;
            const emails = value.split(',');

            for await (const email of emails) {
                let mail = email.trim();
                if (mail.length === 0) {
                    continue;
                }
                if (!this.isValidEmail(mail)) {
                    validme = false;
                    break;
                }
            }
            if (validme === false) {
                this.FormControl.setErrors({ 'email': true });
            } else {
                this.FormControl.setErrors(null);
            }

        }
    }
    isValidEmail(email: string): boolean {
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        return emailRegex.test(email);
    }

    /**
     * Public method to focus the input element
     * Can be called from parent component via @ViewChild
     */
    public focus(): void {

        if (this.inputElement && this.inputElement.nativeElement) {
            this.inputElement.nativeElement.focus();
        }
    }
}

